import { Plus } from "lucide-react";
import {
  useCanAccess,
  useCreate,
  useDelete,
  useGetList,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useId, useState } from "react";
import { AccessDenied } from "@/components/admin/access-denied";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/hooks/use-mobile";

import { htmlToText, textToHtml } from "../mail/mailHtml";
import type { MailTemplate } from "../types";
import { SettingsTabs } from "./SettingsTabs";

const errorMessage = (error: unknown) =>
  (error as { body?: { message?: string } })?.body?.message ??
  (error as Error)?.message ??
  String(error);

/**
 * Настройки → Шаблоны писем (owner only; mail_templates, written through
 * PostgREST, its RLS lets only the owner write). The text is typed as plain
 * text and kept as simple HTML; {{клиент}}, {{сделка}}, {{сумма}} and
 * {{менеджер}} are filled by crm-api when an employee picks the template.
 */
export const MailTemplatesPage = () => {
  const { canAccess, isPending } = useCanAccess({
    resource: "sales",
    action: "edit",
  });
  const isMobile = useIsMobile();
  if (isPending) return null;
  if (!canAccess) return <AccessDenied />;
  return (
    <div className="mt-4 pb-20 flex flex-col gap-6">
      {isMobile ? null : <SettingsTabs />}
      <Templates />
    </div>
  );
};

MailTemplatesPage.path = "/settings/mail-templates";

const Templates = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [create, { isPending: creating }] = useCreate();
  const { data, isPending } = useGetList<MailTemplate>("mail_templates", {
    sort: { field: "id", order: "ASC" },
    pagination: { page: 1, perPage: 100 },
  });

  const add = () =>
    create(
      "mail_templates",
      {
        data: {
          name: translate("crm.mail_templates.new_name"),
          subject: "",
          body_html: "",
        },
      },
      {
        onError: (error) =>
          notify(errorMessage(error), {
            type: "error",
            messageArgs: { _: errorMessage(error) },
          }),
      },
    );

  return (
    <div className="flex flex-col gap-4 max-w-2xl min-w-0">
      <h2 className="vv-heading">{translate("crm.mail_templates.title")}</h2>
      <p className="text-sm text-muted-foreground">
        {translate("crm.mail_templates.hint")}
      </p>
      {isPending ? null : data?.length ? (
        data.map((t) => <TemplateCard key={t.id} template={t} />)
      ) : (
        <p className="text-sm text-muted-foreground">
          {translate("crm.mail_templates.empty")}
        </p>
      )}
      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={creating}
          onClick={add}
        >
          <Plus className="size-4" />
          {translate("crm.mail_templates.add")}
        </Button>
      </div>
    </div>
  );
};

const TemplateCard = ({ template }: { template: MailTemplate }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [update, { isPending: saving }] = useUpdate();
  const [remove, { isPending: deleting }] = useDelete();
  const [name, setName] = useState(template.name);
  const [subject, setSubject] = useState(template.subject);
  const [text, setText] = useState(() => htmlToText(template.body_html));
  const id = useId();
  const fail = (error: unknown) =>
    notify(errorMessage(error), {
      type: "error",
      messageArgs: { _: errorMessage(error) },
    });

  const save = () =>
    update(
      "mail_templates",
      {
        id: template.id,
        data: {
          name: name.trim(),
          subject: subject.trim(),
          body_html: textToHtml(text),
        },
        previousData: template,
      },
      {
        mutationMode: "pessimistic",
        onSuccess: () =>
          notify("crm.mail_templates.saved", { type: "success" }),
        onError: fail,
      },
    );

  return (
    <Card data-template={template.id}>
      <CardContent className="flex flex-col gap-3 min-w-0">
        <div className="flex flex-col gap-1">
          <label
            htmlFor={`${id}-name`}
            className="text-xs text-muted-foreground"
          >
            {translate("crm.mail_templates.name")}
          </label>
          <Input
            id={`${id}-name`}
            value={name}
            maxLength={200}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label
            htmlFor={`${id}-subject`}
            className="text-xs text-muted-foreground"
          >
            {translate("crm.mail_templates.subject")}
          </label>
          <Input
            id={`${id}-subject`}
            value={subject}
            maxLength={300}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label
            htmlFor={`${id}-body`}
            className="text-xs text-muted-foreground"
          >
            {translate("crm.mail_templates.body")}
          </label>
          <Textarea
            id={`${id}-body`}
            value={text}
            rows={6}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={saving || !name.trim()}
            onClick={save}
          >
            {translate("crm.mail_templates.save")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={deleting}
            onClick={() =>
              remove(
                "mail_templates",
                { id: template.id, previousData: template },
                {
                  mutationMode: "pessimistic",
                  onSuccess: () =>
                    notify("crm.mail_templates.deleted", { type: "success" }),
                  onError: fail,
                },
              )
            }
          >
            {translate("crm.mail_templates.delete")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
